import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { aspectRatio, imageGrid, thumbWidth } from '@tradeoutlet/shared';
import { api } from '@/lib/api';

/**
 * Feedback 1 #7: inline image attachments. Tiles reserve their box from the stored width/height (no layout shift),
 * load a server-resized thumbnail lazily, and open a lightbox with prev/next/download. Used by chat (channels,
 * threads, DMs), Kudos and announcements.
 */
export interface GalleryImage { id: string; file_name: string; width?: number | null; height?: number | null; caption?: string | null }
/** Original (or download) URL for the lightbox; chat/kudos use the attachment endpoint, announcements their own. */
export type OriginalUrl = (id: string, download: boolean) => Promise<string | null>;
export const attachmentOriginal: OriginalUrl = async (id, download) => (await api.attachmentUrl(id, download)).url;

const dpr = () => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1);

function Thumb({ img, cssWidth, className, fit = 'cover' }: { img: GalleryImage; cssWidth: number; className?: string; fit?: 'cover' | 'contain' }) {
  const w = thumbWidth(cssWidth, dpr());
  const thumb = useQuery({ queryKey: ['attachment-thumb', img.id, w], queryFn: () => api.attachmentThumbUrl(img.id, w), staleTime: 240_000, retry: false });
  const [fallback, setFallback] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const src = fallback ?? thumb.data?.url;
  return (
    <>
      {!loaded && <span className="absolute inset-0 animate-pulse bg-slate-100 dark:bg-slate-800" aria-hidden />}
      {src && <img src={src} alt={img.caption || img.file_name} loading="lazy" decoding="async" width={img.width ?? undefined} height={img.height ?? undefined}
        onLoad={() => setLoaded(true)}
        onError={() => { if (!fallback) void attachmentOriginal(img.id, false).then(u => u && setFallback(u)).catch(() => undefined); }}
        className={`absolute inset-0 h-full w-full ${fit === 'cover' ? 'object-cover' : 'object-contain'} ${className ?? ''}`} />}
      {thumb.isError && !fallback && <span className="absolute inset-0 grid place-items-center p-2 text-center text-xs text-slate-500">Image unavailable</span>}
    </>
  );
}

/** One image keeps its own shape (max 360×320); 2-4 make a square grid; more shows "+N" on the last tile. */
export function ImageGrid({ images, onOpen, max = 360 }: { images: GalleryImage[]; onOpen: (index: number) => void; max?: number }) {
  if (!images.length) return null;
  const g = imageGrid(images.length);
  if (g.cols === 1) {
    const a = images[0];
    const r = aspectRatio(a);
    const width = Math.round(Math.min(max, 320 * r));
    return (
      <button type="button" onClick={() => onOpen(0)} aria-label={`Open image ${a.file_name}`} data-testid="image-tile"
        className="relative block max-w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 focus-visible:outline focus-visible:outline-2 dark:border-slate-700"
        style={{ width, aspectRatio: String(r) }}>
        <Thumb img={a} cssWidth={width} />
      </button>
    );
  }
  const tile = Math.floor((max - 4) / 2);
  return (
    <div className="grid max-w-full grid-cols-2 gap-1" style={{ width: max }} data-testid="image-grid">
      {images.slice(0, g.tiles).map((a, i) => {
        const last = i === g.tiles - 1 && g.more > 0;
        return (
          <button key={a.id} type="button" onClick={() => onOpen(i)} data-testid="image-tile"
            aria-label={last ? `Open images: ${g.more} more` : `Open image ${a.file_name}`}
            className={`relative overflow-hidden rounded-md bg-slate-50 focus-visible:outline focus-visible:outline-2 dark:bg-slate-800 ${images.length === 3 && i === 0 ? 'col-span-2 aspect-[2/1]' : 'aspect-square'}`}>
            <Thumb img={a} cssWidth={images.length === 3 && i === 0 ? max : tile} />
            {last && <span className="absolute inset-0 grid place-items-center bg-black/55 text-lg font-semibold text-white">+{g.more}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Full-screen viewer: original image, ←/→ and buttons to step, Esc to close, Download. */
export function Lightbox({ images, index, onClose, original = attachmentOriginal }: { images: GalleryImage[]; index: number; onClose: () => void; original?: OriginalUrl }) {
  const [i, setI] = useState(index);
  const close = useRef<HTMLButtonElement>(null);
  const a = images[i];
  const url = useQuery({ queryKey: ['attachment-original', a?.id], queryFn: () => original(a.id, false), enabled: Boolean(a), staleTime: 240_000, retry: false });
  const n = images.length;
  const step = (d: number) => setI(x => (x + d + n) % n);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    close.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' && n > 1) step(1);
      else if (e.key === 'ArrowLeft' && n > 1) step(-1);
    };
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('keydown', key); prev?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!a) return null;
  const download = async () => { const u = await original(a.id, true); if (u) window.open(u, '_blank', 'noopener'); };
  const btn = 'grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white';
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`Image ${i + 1} of ${n}: ${a.file_name}`} data-testid="lightbox"
      className="fixed inset-0 z-[100] flex flex-col bg-black/90" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex items-center gap-3 p-3 text-sm text-white">
        <span className="min-w-0 flex-1 truncate">{a.caption || a.file_name}</span>
        {n > 1 && <span data-testid="lightbox-count" className="tabular-nums text-white/80">{i + 1} of {n}</span>}
        <button type="button" className={btn} onClick={() => void download()} aria-label="Download"><Download className="h-5 w-5" /></button>
        <button ref={close} type="button" className={btn} onClick={onClose} aria-label="Close"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-14 pb-6" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        {url.data ? <img src={url.data} alt={a.caption || a.file_name} className="max-h-full max-w-full object-contain" />
          : url.isError ? <p className="text-white/80">This image couldn’t be loaded.</p>
          : <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-label="Loading" />}
        {n > 1 && <>
          <button type="button" className={`${btn} absolute left-3 top-1/2 -translate-y-1/2`} onClick={() => step(-1)} aria-label="Previous image"><ChevronLeft className="h-6 w-6" /></button>
          <button type="button" className={`${btn} absolute right-3 top-1/2 -translate-y-1/2`} onClick={() => step(1)} aria-label="Next image"><ChevronRight className="h-6 w-6" /></button>
        </>}
      </div>
    </div>,
    document.body,
  );
}

/** Grid + lightbox in one, for lists of attachments. */
export function ImageGallery({ images, max }: { images: GalleryImage[]; max?: number }) {
  const [open, setOpen] = useState<number | null>(null);
  return <>
    <ImageGrid images={images} onOpen={setOpen} max={max} />
    {open !== null && <Lightbox images={images} index={open} onClose={() => setOpen(null)} />}
  </>;
}

/** Browser-side size of a picked image, sent with the upload so the tile is right before the worker confirms it. */
export async function measureImage(file: File): Promise<{ width: number; height: number } | null> {
  if (!/^image\/(png|jpe?g|gif|webp|avif)$/i.test(file.type) || typeof createImageBitmap !== 'function') return null;
  try { const b = await createImageBitmap(file); const r = { width: b.width, height: b.height }; b.close(); return r; } catch { return null; }
}
